import { invoke } from "@tauri-apps/api/core";
import {
  resolvePresentationDragPath,
  resolvePresentationPoint,
} from "@/lib/agent-presentation-contract";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "@/lib/agent-presentation-types";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform, isWindowsPlatform } from "@/lib/os-platform";
import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { getCurrentOwnerEpoch } from "@/lib/owner-state-client";
import { validEditExpectation } from "@/lib/desktop-edit-expectation";
import { validAppCommand } from "@/lib/desktop-app-command";

type InteractionStep = Extract<
  AgentPresentationStep,
  { kind: "click" | "hover" | "drag" | "scroll" | "paste" | "key" | "type" }
>;

type InteractionAction =
  | "click_point"
  | "hover_point"
  | "drag_path"
  | "scroll_at"
  | "press_ui_element"
  | "press_app_command"
  | "paste_text_at_point"
  | "press_key"
  | "menu_ui_element"
  | "key_press"
  | "type_text";

export interface ResolvedPresentationInteraction {
  action: InteractionAction;
  params: Record<string, unknown>;
  targetDisplayId: number | null;
}

/** Whether a semantic target can be acted on rather than only aimed with.
 *
 *  Both shipping platforms can now press a control without moving the pointer -
 *  Accessibility on macOS, UI Automation on Windows - which is the difference
 *  between the agent clicking for the user and the agent taking the mouse away
 *  from them. Anywhere else there is no backend for it, and `desktop_control`
 *  answers with an explicit refusal rather than a silent miss.
 *
 *  Must agree with the `#[cfg]` on the same branch in
 *  `event_receiver_delivery.rs`, which registers the action this delivery is
 *  checked against. */
function semanticPressIsSupported(): boolean {
  return isMacPlatform() || isWindowsPlatform();
}

const SCROLL_LINE_DELTA = 160;
const SCROLL_PAGE_DELTA = 720;

function scrollDelta(step: Extract<InteractionStep, { kind: "scroll" }>) {
  const amount = step.amount === "page" ? SCROLL_PAGE_DELTA : SCROLL_LINE_DELTA;
  switch (step.direction) {
    case "up":
      return { delta_x: 0, delta_y: amount };
    case "down":
      return { delta_x: 0, delta_y: -amount };
    case "left":
      return { delta_x: amount, delta_y: 0 };
    case "right":
      return { delta_x: -amount, delta_y: 0 };
  }
}

export function resolvePresentationInteraction(
  presentation: AgentPresentation,
  step: InteractionStep,
): ResolvedPresentationInteraction | null {
  let action: InteractionAction;
  let params: Record<string, unknown>;
  let targetDisplayId: number | null;

  // Keyboard input has no cursor target: the owner window dispatches it to
  // the focused application after the consent halo and Stop control appear.
  if (
    step.kind === "key" &&
    !step.semantic_target &&
    !step.window_id &&
    step.scope !== "system"
  ) {
    return {
      action: "key_press",
      params: { keys: step.keys },
      targetDisplayId: null,
    };
  }
  if (step.kind === "type") {
    return {
      action: "type_text",
      params: { text: step.text },
      targetDisplayId: null,
    };
  }

  if (step.kind === "drag") {
    const path = resolvePresentationDragPath(step, presentation);
    if (!path || path.length < 2) return null;
    action = "drag_path";
    targetDisplayId = path[0]!.displayId;
    params = {
      path: path.map(({ x, y }) => ({ x, y })),
      duration_ms: step.transition?.duration_ms ?? 600,
    };
  } else {
    const point = resolvePresentationPoint(step, presentation);
    if (!point) return null;
    targetDisplayId = point.displayId;
    if (step.kind === "paste") {
      if (
        step.edit_expectation !== undefined &&
        (!isMacPlatform() ||
          !validEditExpectation(step.edit_expectation) ||
          !step.semantic_target ||
          step.replace === true ||
          step.background === true ||
          step.window_id !== undefined)
      )
        return null;
      action = "paste_text_at_point";
      params = {
        x: point.x,
        y: point.y,
        text: step.text,
        ...(isMacPlatform() ? { background: step.background ?? false } : {}),
        ...(step.replace ? { replace: true } : {}),
        ...(step.edit_expectation !== undefined
          ? { edit_expectation: step.edit_expectation }
          : {}),
        ...(step.semantic_target && semanticPressIsSupported()
          ? { semantic_target: step.semantic_target }
          : {}),
        // No click at all: the text goes where this window's caret is.
        ...(step.window_id ? { window_id: step.window_id } : {}),
      };
    } else if (step.kind === "key") {
      // Keyboard only. The point is where Denker's cursor is drawn and what
      // the display check reads; the key goes to the element or window named.
      action = "press_key";
      params = {
        x: point.x,
        y: point.y,
        keys: step.keys,
        ...(step.repeat !== undefined ? { repeat: step.repeat } : {}),
        ...(step.semantic_target
          ? { semantic_target: step.semantic_target }
          : step.scope === "system"
            ? { scope: "system" }
            : { window_id: step.window_id }),
      };
    } else if (step.kind === "hover") {
      // The pointer goes there and rests; nothing is pressed. A named
      // element is re-read right before the move, as a click's is.
      action = "hover_point";
      params = {
        x: point.x,
        y: point.y,
        ...(step.dwell_ms !== undefined ? { dwell_ms: step.dwell_ms } : {}),
        ...(step.semantic_target
          ? { semantic_target: step.semantic_target }
          : {}),
      };
    } else if (step.kind === "click") {
      if (step.app_command !== undefined) {
        if (
          !isMacPlatform() ||
          !validAppCommand(step.app_command) ||
          step.semantic_target !== undefined ||
          step.background !== undefined ||
          (step.button ?? "left") !== "left" ||
          (step.click_count ?? 1) !== 1
        )
          return null;
        action = "press_app_command";
        params = { x: point.x, y: point.y, app_command: step.app_command };
      } else if (step.semantic_target) {
        if (
          (step.button ?? "left") === "left" &&
          (step.click_count ?? 1) === 1
        ) {
          action = "press_ui_element";
          params = {
            snapshot_id: step.semantic_target.snapshot_id,
            element_id: step.semantic_target.element_id,
            ...(isMacPlatform()
              ? { background: step.background ?? false }
              : {}),
          };
        } else if (
          isMacPlatform() &&
          (step.button ?? "left") === "right" &&
          (step.click_count ?? 1) === 1
        ) {
          // Right single click on a semantic target: try the advertised
          // AXShowMenu action first; the runtime falls back to a physical
          // right-click at the live center when unsupported.
          action = "menu_ui_element";
          params = {
            x: point.x,
            y: point.y,
            button: "right",
            click_count: 1,
            semantic_target: step.semantic_target,
            background: step.background ?? false,
          };
        } else {
          // Double click on a semantic target: a physical click. The point
          // is where the cursor is drawn; the target goes along so the
          // runtime re-reads the element right before the click and clicks
          // where it is then, even if its window moved meanwhile.
          action = "click_point";
          params = {
            x: point.x,
            y: point.y,
            ...(step.button !== undefined ? { button: step.button } : {}),
            ...(step.click_count !== undefined
              ? { click_count: step.click_count }
              : {}),
            semantic_target: step.semantic_target,
          };
        }
      } else {
        action = "click_point";
        params = {
          x: point.x,
          y: point.y,
          ...(step.button !== undefined ? { button: step.button } : {}),
          ...(step.click_count !== undefined
            ? { click_count: step.click_count }
            : {}),
        };
      }
    } else {
      action = "scroll_at";
      params = {
        x: point.x,
        y: point.y,
        ...scrollDelta(step),
        duration_ms: step.transition?.duration_ms ?? 240,
        background: step.background ?? false,
        ...(step.semantic_target
          ? {
              semantic_target: step.semantic_target,
              direction: step.direction,
              amount: step.amount,
            }
          : {}),
      };
    }
  }

  return { action, params, targetDisplayId };
}

export async function performPresentationInteraction(
  presentation: AgentPresentation,
  step: InteractionStep,
  signal: AbortSignal,
  onFailure?: (reason: string) => void,
): Promise<boolean> {
  if (signal.aborted || !isTauri()) return false;
  const resolved = resolvePresentationInteraction(presentation, step);
  if (!resolved) return false;

  // Every display window receives the choreography. The target display is
  // responsible for native input and the completion acknowledgement.
  const displayId = getWindowDisplayId();
  const responsible =
    resolved.targetDisplayId !== null
      ? displayId === resolved.targetDisplayId
      : getWindowRoleSync() === "owner";
  if (!responsible) return true;
  if (displayId === null || !presentation.executionId) return false;

  try {
    const result = await invoke<Record<string, unknown>>(
      "desktop_foreground_action",
      {
        interaction: {
          presentationId: presentation.presentationId,
          executionId: presentation.executionId,
          stepId: step.step_id,
          displayId,
          ownerEpoch: getCurrentOwnerEpoch(),
          action: resolved.action,
          params: resolved.params,
        },
      },
    );
    if (
      step.kind === "click" &&
      step.app_command !== undefined &&
      (result.status !== "action_dispatched" ||
        result.route_id !== "accessibility.app_command" ||
        result.method !== "accessibility_app_command" ||
        result.moves_system_cursor !== false ||
        result.input_attempted !== true ||
        result.window_id !== step.app_command.window_id ||
        result.discovery_id !== step.app_command.discovery_id ||
        result.command_id !== step.app_command.command_id)
    ) {
      onFailure?.(
        "Native command did not report dispatch; inspect its receipt before any retry",
      );
      return false;
    }
    if (step.kind === "scroll" && result.status !== "ok") {
      onFailure?.("Native scroll did not report success");
      return false;
    }
    if (step.kind === "paste") {
      if (step.edit_expectation !== undefined) {
        const verification = result.edit_verification as
          | { verified?: unknown; scope?: unknown; route?: unknown }
          | undefined;
        if (
          result.status !== "ok" ||
          result.paste_verified !== true ||
          result.method !== "accessibility_selected_text" ||
          result.route_id !== "accessibility.guarded_selection_edit" ||
          verification?.verified !== true ||
          verification.scope !== "focused_selection_and_plain_text" ||
          verification.route !== "accessibility_selected_text"
        ) {
          onFailure?.(
            "Guarded edit lacks exact native verification; inspect its receipt before any retry",
          );
          return false;
        }
      }
      // Three answers, not two. "ok" means the text was read back out of the
      // field. "not_delivered" means the field was read and contradicts it, or
      // the caret was placed by clicking a position and the target will not say
      // what it holds - either way there is no reason to believe it arrived.
      //
      // "sent_unverified" is the one that used to be lumped in with failure and
      // is not one: the companion focused the element the agent NAMED, confirmed
      // it took the keyboard, and sent the text - and then the control declined
      // to report its contents, which is what every widget that transforms what
      // you type does. A recipient box that turns an address into a chip reads
      // back empty after a paste that worked.
      //
      // Calling that a failure cost a measured run four minutes: seven attempts
      // at one field, then the other fields, then discarding the draft and
      // starting over, every attempt having actually succeeded. The reply
      // carries delivery_note telling the agent how to check rather than retry.
      if (result.status === "not_delivered") {
        onFailure?.(
          typeof result.delivery_note === "string"
            ? result.delivery_note
            : "Native paste was not delivered",
        );
        return false;
      }
      if (result.status !== "ok" && result.status !== "sent_unverified") {
        onFailure?.(`Native paste reported ${String(result.status)}`);
        return false;
      }
      return !signal.aborted;
    }
    if (step.kind === "key" && result.status !== "ok") {
      onFailure?.(`Key press reported ${String(result.status)}`);
      return false;
    }
    if (step.kind === "key") {
      return !signal.aborted && result.status === "ok";
    }
    if (step.kind === "type") {
      // sent_unverified still delivered input; verification evidence rides
      // along for the caller to judge instead of failing the presentation.
      return (
        !signal.aborted &&
        (result.status === "ok" || result.status === "sent_unverified")
      );
    }
    return !signal.aborted;
  } catch (error) {
    const reason =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Native desktop action failed";
    onFailure?.(reason.slice(0, 1000));
    return false;
  }
}
